// Location Service for Arya Web App
// Provides location detection and management with user permissions

class LocationService {
  constructor() {
    this.currentLocation = null;
    this.locationPermission = null;
    this.watcherId = null;
    this.callbacks = [];
    this.isSupported = "geolocation" in navigator;
  }

  // Request location permission and get current location
  async requestLocationPermission() {
    if (!this.isSupported) {
      throw new Error("Geolocation is not supported by this browser");
    }

    try {
      // Force permission request by setting maximumAge to 0
      const position = await this.getCurrentPosition(true);
      this.currentLocation = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: new Date().toISOString(),
      };
      this.locationPermission = "granted";
      this.storePermissionDecision("granted");
      this.notifyCallbacks();
      return this.currentLocation;
    } catch (error) {
      this.locationPermission = "denied";
      this.storePermissionDecision("denied");

      // Provide more helpful error messages
      let errorMessage = error.message;
      if (error.code === error.PERMISSION_DENIED) {
        errorMessage =
          "Location permission was denied. Please enable location access in your browser settings and try again.";
      } else if (error.code === error.POSITION_UNAVAILABLE) {
        errorMessage =
          "Location information is unavailable. Please check your device's location settings.";
      } else if (error.code === error.TIMEOUT) {
        errorMessage = "Location request timed out. Please try again.";
      }

      const enhancedError = new Error(errorMessage);
      enhancedError.originalError = error;
      enhancedError.code = error.code;
      throw enhancedError;
    }
  }

  // Get current position with high accuracy
  getCurrentPosition(forcePermission = false) {
    return new Promise((resolve, reject) => {
      const options = {
        enableHighAccuracy: true,
        timeout: 10000,
        // Force permission request by setting maximumAge to 0
        maximumAge: forcePermission ? 0 : 300000, // 5 minutes
      };

      navigator.geolocation.getCurrentPosition(resolve, reject, options);
    });
  }

  // Start watching location changes
  startWatching() {
    if (!this.isSupported || this.watcherId) {
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 60000, // 1 minute
    };

    this.watcherId = navigator.geolocation.watchPosition(
      (position) => {
        this.currentLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date().toISOString(),
        };
        this.notifyCallbacks();
      },
      (error) => {
        console.error("Location watch error:", error);
      },
      options
    );
  }

  // Stop watching location changes
  stopWatching() {
    if (this.watcherId) {
      navigator.geolocation.clearWatch(this.watcherId);
      this.watcherId = null;
    }
  }

  // Get current location with name (reverse geocoding)
  async getCurrentLocationWithName() {
    if (!this.currentLocation) {
      return null;
    }

    try {
      const locationName = await this.getLocationName(
        this.currentLocation.latitude,
        this.currentLocation.longitude
      );
      return {
        ...this.currentLocation,
        name: locationName,
      };
    } catch (error) {
      console.error("Error getting location name:", error);
      return this.currentLocation;
    }
  }

  // Get location name from coordinates (reverse geocoding)
  async getLocationName(latitude, longitude) {
    try {
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
      );
      const data = await response.json();

      if (data.city && data.principalSubdivision) {
        return `${data.city}, ${data.principalSubdivision}`;
      } else if (data.locality) {
        return data.locality;
      } else if (data.principalSubdivision) {
        return data.principalSubdivision;
      } else if (data.countryName) {
        return data.countryName;
      }
      return "Unknown Location";
    } catch (error) {
      console.error("Reverse geocoding error:", error);
      return "Unknown Location";
    }
  }

  // Subscribe to location changes
  subscribe(callback) {
    this.callbacks.push(callback);
    return () => {
      this.callbacks = this.callbacks.filter((cb) => cb !== callback);
    };
  }

  // Notify all subscribers of location changes
  notifyCallbacks() {
    this.callbacks.forEach((callback) => {
      try {
        callback(this.currentLocation);
      } catch (error) {
        console.error("Location callback error:", error);
      }
    });
  }

  // Check if we have a current location
  hasLocation() {
    return this.currentLocation !== null;
  }

  // Get current location
  getCurrentLocation() {
    return this.currentLocation;
  }

  // Get permission status
  getPermissionStatus() {
    return this.locationPermission;
  }

  // Store location in localStorage
  storeLocation(location) {
    try {
      localStorage.setItem("arya_location", JSON.stringify(location));
    } catch (error) {
      console.error("Error storing location:", error);
    }
  }

  // Get stored location from localStorage
  getStoredLocation() {
    try {
      const stored = localStorage.getItem("arya_location");
      if (stored) {
        const location = JSON.parse(stored);
        // Check if stored location is not too old (24 hours)
        const age = Date.now() - new Date(location.timestamp).getTime();
        if (age < 24 * 60 * 60 * 1000) {
          return location;
        }
      }
    } catch (error) {
      console.error("Error reading stored location:", error);
    }
    return null;
  }

  // Initialize location service
  async initialize() {
    try {
      // Try to get stored location first
      const storedLocation = this.getStoredLocation();
      if (storedLocation) {
        this.currentLocation = storedLocation;
        this.locationPermission = "granted";
        return storedLocation;
      }

      // If no stored location, try to get current location
      const location = await this.requestLocationPermission();
      this.storeLocation(location);
      return location;
    } catch (error) {
      console.error("Failed to initialize location:", error);
      return null;
    }
  }

  // Clear stored location
  clearStoredLocation() {
    localStorage.removeItem("arya_location");
    this.currentLocation = null;
    this.locationPermission = null;
  }

  // Check if we should auto-request location permission
  shouldAutoRequestLocation() {
    // Don't auto-request if geolocation is not supported
    if (!this.isSupported) return false;

    // Don't auto-request if permission is already granted
    if (this.locationPermission === "granted") return false;

    // Don't auto-request if we have stored location data
    if (this.hasLocation()) return false;

    // Don't auto-request if permission was explicitly denied
    if (this.locationPermission === "denied") return false;

    return true;
  }

  // Check if this is the first time the user is using the app
  isFirstTimeUser() {
    const hasStoredLocation = localStorage.getItem("arya_location");
    const hasPermissionHistory = localStorage.getItem(
      "arya_location_permission"
    );
    return !hasStoredLocation && !hasPermissionHistory;
  }

  // Store permission decision for future reference
  storePermissionDecision(permission) {
    try {
      localStorage.setItem("arya_location_permission", permission);
    } catch (error) {
      console.error("Error storing permission decision:", error);
    }
  }

  // Get stored permission decision
  getStoredPermissionDecision() {
    try {
      return localStorage.getItem("arya_location_permission");
    } catch (error) {
      console.error("Error reading stored permission decision:", error);
      return null;
    }
  }

  // Check if location permission is blocked by browser
  async checkLocationPermissionStatus() {
    if (!this.isSupported) {
      return { status: "unsupported", message: "Geolocation not supported" };
    }

    try {
      // Try to get current position with a very short timeout and force fresh permission
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 1000,
          maximumAge: 0, // Force fresh permission request
          enableHighAccuracy: false, // Faster response for permission check
        });
      });

      return { status: "granted", message: "Location permission granted" };
    } catch (error) {
      if (error.code === error.PERMISSION_DENIED) {
        return {
          status: "denied",
          message:
            "Location permission denied. Please enable in browser settings.",
          code: error.code,
        };
      } else if (error.code === error.POSITION_UNAVAILABLE) {
        return {
          status: "unavailable",
          message: "Location unavailable. Check device location settings.",
          code: error.code,
        };
      } else if (error.code === error.TIMEOUT) {
        return {
          status: "timeout",
          message: "Location request timed out.",
          code: error.code,
        };
      }

      return {
        status: "error",
        message: error.message,
        code: error.code,
      };
    }
  }

  // Reset location permission (clear stored data)
  resetLocationPermission() {
    this.clearStoredLocation();
    localStorage.removeItem("arya_location_permission");
    this.locationPermission = null;
    console.log("Location permission data cleared. You can now try again.");
  }

  // Force a fresh location permission request (bypasses any cached data)
  async forceLocationPermissionRequest() {
    if (!this.isSupported) {
      throw new Error("Geolocation is not supported by this browser");
    }

    try {
      // Clear any stored data first
      this.resetLocationPermission();

      // Force a fresh permission request with no cached data
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000, // Longer timeout for fresh request
          maximumAge: 0, // Force fresh permission request
        });
      });

      this.currentLocation = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: new Date().toISOString(),
      };
      this.locationPermission = "granted";
      this.storePermissionDecision("granted");
      this.notifyCallbacks();
      return this.currentLocation;
    } catch (error) {
      this.locationPermission = "denied";
      this.storePermissionDecision("denied");

      // Provide more helpful error messages
      let errorMessage = error.message;
      if (error.code === error.PERMISSION_DENIED) {
        errorMessage =
          "Location permission was denied. Please click 'Allow' when your browser asks for location access.";
      } else if (error.code === error.POSITION_UNAVAILABLE) {
        errorMessage =
          "Location information is unavailable. Please check your device's location settings.";
      } else if (error.code === error.TIMEOUT) {
        errorMessage = "Location request timed out. Please try again.";
      }

      const enhancedError = new Error(errorMessage);
      enhancedError.originalError = error;
      enhancedError.code = error.code;
      throw enhancedError;
    }
  }

  // Get browser location permission instructions
  getLocationPermissionInstructions() {
    const userAgent = navigator.userAgent.toLowerCase();

    if (userAgent.includes("chrome")) {
      return {
        browser: "Chrome",
        instructions: [
          "Click the lock icon in the address bar",
          'Select "Allow" for Location',
          "Refresh the page and try again",
        ],
      };
    } else if (userAgent.includes("firefox")) {
      return {
        browser: "Firefox",
        instructions: [
          "Click the shield icon in the address bar",
          'Click "Allow" for Location',
          "Refresh the page and try again",
        ],
      };
    } else if (userAgent.includes("safari")) {
      return {
        browser: "Safari",
        instructions: [
          "Go to Safari > Preferences > Websites > Location",
          'Set this website to "Allow"',
          "Refresh the page and try again",
        ],
      };
    } else if (userAgent.includes("edge")) {
      return {
        browser: "Edge",
        instructions: [
          "Click the lock icon in the address bar",
          'Select "Allow" for Location',
          "Refresh the page and try again",
        ],
      };
    } else {
      return {
        browser: "Unknown",
        instructions: [
          "Look for a location permission icon in your browser",
          "Allow location access for this website",
          "Refresh the page and try again",
        ],
      };
    }
  }
}

// Create singleton instance
const locationService = new LocationService();

export default locationService;
